import type { ComputedRef, Ref } from "vue";
import type { Event } from "~/types/event";
import { stripHtml } from "@donoor/utils/string";
import { useChatPageContext, euros, dateLisible, phrasePaysAssociation } from "~/composables/useChatPageContext";

interface TarifResume {
  pricing?: string | null;
  price: number;
  min_price?: number | null;
}

interface EtatBilletterie {
  ticketTypes: ComputedRef<TarifResume[]>;
  /** Billetterie fermée par la date ou par l'association (useEventStatus). */
  isEventEnded: Ref<boolean>;
  isClosed: Ref<boolean>;
  /** Places restantes, toutes catégories (null = sans limite) — useEventTicketSelection. */
  totalRemainingPlaces: Ref<number | null>;
  hasAvailableTickets: Ref<boolean>;
  isWaitlistEnabled: Ref<boolean>;
}

/**
 * Pousse le résumé de l'événement affiché vers le contexte du chat IA
 * (`useChatPageContext`) : elle aide en priorité dessus tant que le visiteur
 * ne change pas de sujet (Karim 2026-10-12). Extrait de `pages/b/[slug]`
 * (limite 800 lignes).
 *
 * Lieu complet, ventes ouvertes ou non et places restantes (27/10/2026) :
 * sans eux, l'IA répondait « je ne sais pas » à « c'est dans quelle ville ? »,
 * « reste-t-il des places ? » ou « les inscriptions sont fermées ? »
 * (conversations du 17 au 21/10/2026).
 */
export function useEventChatContext(event: Ref<Event | null | undefined>, etat: EtatBilletterie): void {
  const { definirContexte, effacerContexte } = useChatPageContext();
  watchEffect(() => {
    const e = event.value;
    if (!e) return;
    const debut = dateLisible(e.start_date);
    const fin = dateLisible(e.end_date);
    const types = etat.ticketTypes.value;
    const prixMin = types.length
      ? Math.min(...types.map((tt) => (tt.pricing === "flexible" ? (tt.min_price ?? tt.price) : tt.price)))
      : null;
    // Nom du lieu, adresse et ville, sans répéter un morceau déjà contenu dans un autre.
    const lieu = [e.location_name, e.location_address, e.location_city]
      .map((x) => x?.trim())
      .filter((x, i, tous): x is string => !!x && !tous.slice(0, i).some((y) => y?.includes(x)));
    const restantes = etat.totalRemainingPlaces.value;
    const ventes = etat.isEventEnded.value
      ? "L'événement est terminé : la billetterie en ligne est fermée."
      : etat.isClosed.value
        ? "La billetterie est clôturée : plus aucun billet n'est en vente en ligne."
        : !types.length
          ? null
          : !etat.hasAvailableTickets.value
            ? `Tous les billets sont vendus (complet)${etat.isWaitlistEnabled.value ? " ; une liste d'attente est proposée sur la page" : ""}.`
            : restantes !== null
              ? `Billets en vente : ${restantes} place(s) restante(s).`
              : "Billets en vente.";
    const phrases = [
      `Événement « ${e.title} »${e.organization_name ? ` de l'association ${e.organization_name}` : ""}.`,
      debut ? (fin && fin !== debut ? `Du ${debut} au ${fin}.` : `Le ${debut}.`) : null,
      lieu.length ? `Lieu : ${lieu.join(", ")}.` : null,
      types.length
        ? prixMin === 0
          ? "Billets gratuits."
          : `Billets à partir de ${euros(prixMin)}.`
        : "Aucun billet disponible pour le moment.",
      ventes,
      e.description ? `Description : ${stripHtml(e.description).slice(0, 600)}` : null,
      phrasePaysAssociation(e.country),
    ].filter(Boolean);
    definirContexte("evenement", phrases.join(" "));
  });
  onUnmounted(() => effacerContexte());
}
